import { Message } from '@/types';

// Lista expandida de termos jurídicos para detecção inteligente
const LEGAL_TERMS = [
  // Documentos e Contratos
  'contrato', 'cláusula', 'acordo', 'termo', 'aditivo', 'rescisão', 'locação', 'compra', 'venda',
  'sociedade', 'prestação', 'serviços', 'cessão', 'direitos', 'obrigações', 'garantia', 'fiança',
  
  // Processual
  'petição', 'inicial', 'contestação', 'tréplica', 'recurso', 'apelação', 'embargos', 'agravo',
  'mandado', 'liminar', 'sentença', 'acórdão', 'decisão', 'despacho', 'intimação', 'citação',
  
  // Jurisprudência e Precedentes
  'jurisprudência', 'precedente', 'súmula', 'enunciado', 'tese', 'orientação', 'entendimento',
  'julgado', 'tribunal', 'stj', 'stf', 'tjsp', 'tjrj', 'tjmg', 'tjrs', 'tjsc', 'tjpr',
  
  // Áreas do Direito
  'civil', 'penal', 'trabalhista', 'tributário', 'administrativo', 'constitucional', 'empresarial',
  'previdenciário', 'consumidor', 'ambiental', 'eleitoral', 'internacional', 'digital',
  
  // Termos Técnicos
  'responsabilidade', 'indenização', 'danos', 'lucros', 'cessantes', 'moral', 'material',
  'prescrição', 'decadência', 'precatório', 'execução', 'penhora', 'arresto', 'sequestro',
  
  // Legislação
  'lei', 'código', 'decreto', 'portaria', 'resolução', 'instrução', 'normativa', 'medida',
  'provisória', 'constituição', 'artigo', 'parágrafo', 'inciso', 'alínea', 'cpc', 'cc', 'clt',
  
  // Procedimentos
  'protocolo', 'distribuição', 'autuação', 'juntada', 'vista', 'carga', 'baixa', 'arquivo',
  'desarquivamento', 'redistribuição', 'remessa', 'devolução', 'cumprimento', 'liquidação',
  
  // Partes e Pessoas
  'autor', 'réu', 'requerente', 'requerido', 'apelante', 'apelado', 'embargante', 'embargado',
  'cliente', 'advogado', 'procurador', 'representante', 'curador', 'tutor', 'inventariante',
  
  // Valores e Cálculos
  'honorários', 'custas', 'multa', 'juros', 'correção', 'monetária', 'índice', 'atualização',
  'valor', 'causa', 'alçada', 'competência', 'foro', 'vara', 'turma', 'câmara', 'seção',
  
  // Prazos
  'prazo', 'término', 'vencimento', 'dilação', 'prorrogação', 'suspensão', 'interrupção',
  'dies', 'inicial', 'final', 'útil', 'corrido', 'judicial', 'extrajudicial'
];

// Palavras de ação jurídica
const ACTION_WORDS = [
  'criar', 'elaborar', 'redigir', 'analisar', 'revisar', 'corrigir', 'ajustar', 'modificar',
  'buscar', 'pesquisar', 'consultar', 'verificar', 'conferir', 'validar', 'interpretar',
  'calcular', 'computar', 'atualizar', 'indexar', 'aplicar', 'incidência', 'base',
  'fundamentar', 'embasar', 'justificar', 'argumentar', 'defender', 'impugnar', 'contestar'
];

// Contextos que indicam complexidade
const COMPLEXITY_INDICATORS = [
  'análise', 'parecer', 'relatório', 'estudo', 'pesquisa', 'investigação', 'levantamento',
  'comparativo', 'avaliação', 'diagnóstico', 'prognóstico', 'estratégia', 'planejamento',
  'múltiplas', 'diversas', 'várias', 'complexo', 'detalhado', 'aprofundado', 'especializado'
];

export interface ComplexityAnalysis {
  level: 'simple' | 'medium' | 'complex';
  score: number;
  reasons: string[];
  legalTermsFound: string[];
  actionWordsFound: string[];
  complexityIndicators: string[];
  shouldUseCoT: boolean;
  estimatedProcessingTime: string;
}

export function analyzeMessageComplexity(
  message: string,
  conversationHistory: Message[] = [],
  hasDocument: boolean = false
): ComplexityAnalysis {
  const normalizedMessage = message.toLowerCase().trim();
  const words = normalizedMessage.split(/\s+/);
  
  // Detectar termos jurídicos
  const legalTermsFound = LEGAL_TERMS.filter(term => 
    normalizedMessage.includes(term.toLowerCase())
  );
  
  // Detectar palavras de ação
  const actionWordsFound = ACTION_WORDS.filter(action => 
    normalizedMessage.includes(action.toLowerCase())
  );
  
  // Detectar indicadores de complexidade
  const complexityIndicators = COMPLEXITY_INDICATORS.filter(indicator => 
    normalizedMessage.includes(indicator.toLowerCase())
  );
  
  // Calcular score base
  let score = 0;
  const reasons: string[] = [];
  
  // Pontuação por termos jurídicos (peso alto)
  score += legalTermsFound.length * 15;
  if (legalTermsFound.length > 0) {
    reasons.push(`${legalTermsFound.length} termos jurídicos encontrados`);
  }
  
  // Pontuação por palavras de ação (peso médio)
  score += actionWordsFound.length * 10;
  if (actionWordsFound.length > 0) {
    reasons.push(`${actionWordsFound.length} palavras de ação detectadas`);
  }
  
  // Pontuação por indicadores de complexidade (peso alto)
  score += complexityIndicators.length * 20;
  if (complexityIndicators.length > 0) {
    reasons.push(`${complexityIndicators.length} indicadores de complexidade`);
  }
  
  // Pontuação por tamanho da mensagem
  if (words.length > 50) {
    score += 25;
    reasons.push('Mensagem longa (>50 palavras)');
  } else if (words.length > 20) {
    score += 15;
    reasons.push('Mensagem média (>20 palavras)');
  }
  
  // Pontuação por presença de documento
  if (hasDocument) {
    score += 30;
    reasons.push('Documento anexado na conversa');
  }
  
  // Pontuação por contexto da conversa
  if (conversationHistory.length > 5) {
    score += 10;
    reasons.push('Conversa com histórico extenso');
  }
  
  // Detectar padrões específicos
  if (normalizedMessage.includes('jurisprudência') || normalizedMessage.includes('precedente')) {
    score += 25;
    reasons.push('Solicitação de jurisprudência detectada');
  }
  
  if (normalizedMessage.includes('contrato') && (
    normalizedMessage.includes('criar') || 
    normalizedMessage.includes('elaborar') || 
    normalizedMessage.includes('redigir')
  )) {
    score += 30;
    reasons.push('Criação de documento jurídico detectada');
  }
  
  // Determinar nível de complexidade
  let level: 'simple' | 'medium' | 'complex';
  let shouldUseCoT = false;
  let estimatedProcessingTime: string;
  
  if (score <= 20) {
    level = 'simple';
    estimatedProcessingTime = '<1s';
  } else if (score <= 60) {
    level = 'medium';
    estimatedProcessingTime = '3-5s';
  } else {
    level = 'complex';
    shouldUseCoT = true;
    estimatedProcessingTime = '8-15s';
  }
  
  // Casos especiais que forçam CoT
  if (legalTermsFound.length >= 3 || 
      complexityIndicators.length >= 2 || 
      (hasDocument && actionWordsFound.length >= 1)) {
    shouldUseCoT = true;
    if (level === 'medium') {
      level = 'complex';
      estimatedProcessingTime = '8-15s';
    }
  }
  
  return {
    level,
    score,
    reasons,
    legalTermsFound,
    actionWordsFound,
    complexityIndicators,
    shouldUseCoT,
    estimatedProcessingTime
  };
}

// Cache para respostas simples
export const QUICK_RESPONSE_CACHE = new Map<string, string>([
  ['oi', 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo hoje?'],
  ['olá', 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo hoje?'],
  ['ola', 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo hoje?'],
  ['bom dia', 'Bom dia! Em que posso ajudá-lo juridicamente hoje?'],
  ['boa tarde', 'Boa tarde! Estou aqui para auxiliá-lo com suas demandas jurídicas.'],
  ['boa noite', 'Boa noite! Como posso ajudá-lo com questões jurídicas hoje?'],
  ['olá, boa noite', 'Boa noite! Como posso ajudá-lo com questões jurídicas hoje?'],
  ['ola, boa noite', 'Boa noite! Como posso ajudá-lo com questões jurídicas hoje?'],
  ['obrigado', 'De nada! Estou sempre aqui para ajudar com suas questões jurídicas.'],
  ['obrigada', 'De nada! Estou sempre aqui para ajudar com suas questões jurídicas.'],
  ['tchau', 'Até logo! Sempre que precisar de assistência jurídica, estarei aqui.'],
  ['até logo', 'Até logo! Sempre que precisar de assistência jurídica, estarei aqui.'],
  // Respostas sobre modelo/inteligência - LAIB 3.7
  ['qual modelo', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['que modelo', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['qual inteligencia', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['que inteligencia', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['qual tecnologia', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['backend', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['como você funciona', 'Funciono utilizando o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada.'],
  // Respostas sobre criador/desenvolvedor
  ['quem criou você', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem criou voce', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem te criou', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem desenvolveu você', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem desenvolveu voce', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem te desenvolveu', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem é seu criador', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem e seu criador', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  // Variações coloquiais sobre fornecedor/modelo (30+ entradas)
  ['vc é da openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['você é da openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['voce e da openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['usa openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['usa gpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['é gpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['e gpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['é chatgpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['e chatgpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['usa chatgpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['qual modelo você usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['qual modelo voce usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['que modelo você usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['que modelo voce usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['vc usa gpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['você usa gpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['vc é gpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['você é gpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['vc é chatgpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['você é chatgpt', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem fez vc', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem fez você', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['quem fez voce', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['é da openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['e da openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['vc usa openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['você usa openai', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['usa claude', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['usa gemini', 'Sou o **Justiçai**, desenvolvido pela **Octopus Group** com o modelo **Laib 3.7**, especializado em análise jurídica brasileira.'],
  ['qual ia você usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['qual ia voce usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['que ia você usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.'],
  ['que ia voce usa', 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.']
]);

export function getQuickResponse(message: string): string | null {
  const normalized = message.toLowerCase().trim();
  
  // ✅ CORREÇÃO: Verificação EXATA apenas (sem includes para evitar falsos positivos)
  const exactMatch = QUICK_RESPONSE_CACHE.get(normalized);
  if (exactMatch) {
    console.log(`⚡ Resposta rápida encontrada para: "${normalized}"`);
    return exactMatch;
  }
  
  // 🔒 REMOVIDO: Verificação por includes() que causava falsos positivos
  // Exemplo: "proibido" continha "oi" e acionava cumprimento indevidamente
  
  return null; // Se não for match exato, processar pela IA
}